import { useState } from "react";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";

const MARGIN = 20;
const BLOCK_GAP = 4;
const PIXEL_RATIO = 2;
const MAX_BLOCK_HEIGHT = 1000;
const THEME_SETTLE_MS = 100;
const REMOVE_SELECTORS = [".feedback", ".message-edit", ".table-block__export"];
const OVERFLOW_SELECTORS = ".table-responsive, .heatmap-block__scroll";

// --- Image helpers ---

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const placeImage = (pdf, src, y, width, height) =>
  pdf.addImage(src, "PNG", MARGIN, y, width, height, undefined, "FAST");

const cropSlice = (img, srcWidth, srcOffset, srcSliceHeight) => {
  const roundedOffset = Math.round(srcOffset);
  const roundedHeight = Math.round(srcSliceHeight);

  const canvas = document.createElement("canvas");
  canvas.width = srcWidth;
  canvas.height = roundedHeight;

  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, roundedOffset, srcWidth, roundedHeight, 0, 0, srcWidth, roundedHeight);

  const dataUrl = canvas.toDataURL("image/png");
  canvas.width = 0;
  canvas.height = 0;

  return dataUrl;
};

const captureBlockImage = (block) => {
  const blockWidth = Math.max(block.offsetWidth, block.scrollWidth);
  return toPng(block, {
    pixelRatio: PIXEL_RATIO,
    backgroundColor: "#ffffff",
    width: blockWidth,
    skipFonts: true,
    fontEmbedCSS: "",
    style: {
      width: `${blockWidth}px`,
      margin: "0",
      padding: "0",
      left: "0",
      top: "0",
      position: "relative",
      overflow: "visible"
    }
  });
};

// --- Table cut-point helpers ---

const findSafeCut = (safeCutPoints, displayOffset, maxCut) => {
  const EPSILON = 0.5;
  for (let j = safeCutPoints.length - 1; j >= 0; j--) {
    if (safeCutPoints[j] <= maxCut + EPSILON && safeCutPoints[j] > displayOffset + EPSILON) {
      return safeCutPoints[j];
    }
  }
  return null;
};

const getTableCutPoints = (block, contentWidth) => {
  const tableEl = block.querySelector("table");
  if (!tableEl) return null;

  const blockRect = block.getBoundingClientRect();
  const pdfScale = contentWidth / block.offsetWidth;
  return Array.from(tableEl.querySelectorAll("tr")).map(
    (tr) => Math.round((tr.getBoundingClientRect().bottom - blockRect.top) * pdfScale * 100) / 100
  );
};

// --- PDF page slicing (tables only) ---

const addImageSliced = async ({
  pdf,
  dataUrl,
  startY,
  width,
  height,
  srcWidth,
  srcHeight,
  pageHeight,
  safeCutPoints
}) => {
  const availableSpace = pageHeight - MARGIN - startY;

  if (height <= availableSpace) {
    placeImage(pdf, dataUrl, startY, width, height);
    return startY + height;
  }

  const scale = srcHeight / height;
  const fullPageSlice = pageHeight - MARGIN * 2;
  const img = await loadImage(dataUrl);

  let remainingHeight = height;
  let srcOffset = 0;
  let currentSliceHeight = availableSpace;
  let y = startY;

  while (remainingHeight > 0) {
    let sliceHeight = Math.min(currentSliceHeight, remainingHeight);

    if (safeCutPoints && remainingHeight > sliceHeight) {
      const displayOffset = height - remainingHeight;
      const bestCut = findSafeCut(safeCutPoints, displayOffset, displayOffset + sliceHeight);
      if (bestCut !== null) {
        sliceHeight = bestCut - displayOffset;
      }
    }

    const srcSliceHeight = sliceHeight * scale;
    const sliceDataUrl = cropSlice(img, srcWidth, srcOffset, srcSliceHeight);
    placeImage(pdf, sliceDataUrl, y, width, sliceHeight);

    remainingHeight -= sliceHeight;
    srcOffset += srcSliceHeight;

    if (remainingHeight > 0) {
      pdf.addPage();
      y = MARGIN;
      currentSliceHeight = fullPageSlice;
    } else {
      y += sliceHeight;
    }
  }

  return y;
};

// --- DOM sandbox ---

const createSandbox = () => {
  const clone = document.querySelector(".ai-agent").cloneNode(true);

  REMOVE_SELECTORS.forEach((sel) => clone.querySelectorAll(sel).forEach((el) => el.remove()));

  clone.querySelectorAll(OVERFLOW_SELECTORS).forEach((el) => {
    el.style.overflow = "visible";
    el.style.maxHeight = "none";
    el.style.height = "auto";
  });

  const sandbox = document.createElement("div");
  sandbox.innerHTML = "<div class='light' style='position:absolute; left:-9999px;'></div>";
  sandbox.querySelector(".light").appendChild(clone);
  document.body.appendChild(sandbox);

  return { sandbox, clone };
};

const buildCaptureList = (clone) => {
  const elements = [];

  clone.querySelectorAll(".message-wrapper").forEach((wrapper) => {
    if (wrapper.offsetHeight <= MAX_BLOCK_HEIGHT) {
      elements.push(wrapper);
      return;
    }

    const messageText = wrapper.querySelector(".message-text");
    if (messageText && messageText.children.length > 1) {
      elements.push(...Array.from(messageText.children));
    } else {
      elements.push(wrapper);
    }
  });

  return elements;
};

// --- Hook ---

export default function usePdf(ref) {
  const [loading, setLoading] = useState(false);

  const exportPdf = async () => {
    if (!ref.current) return;

    setLoading(true);

    const { sandbox, clone } = createSandbox();

    try {
      await new Promise((resolve) => setTimeout(resolve, THEME_SETTLE_MS));

      const pdf = new jsPDF("p", "px", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const contentWidth = pageWidth - MARGIN * 2;
      let currentY = MARGIN;

      const captureElements = buildCaptureList(clone);

      for (let i = 0; i < captureElements.length; i++) {
        const block = captureElements[i];
        if (block.offsetHeight === 0) continue;

        const dataUrl = await captureBlockImage(block);
        const { width: srcWidth, height: srcHeight } = pdf.getImageProperties(dataUrl);
        const imgHeight = Math.round(((srcHeight * contentWidth) / srcWidth) * 100) / 100;

        if (block.classList.contains("table-block")) {
          const safeCutPoints = getTableCutPoints(block, contentWidth);
          currentY = await addImageSliced({
            pdf,
            dataUrl,
            startY: currentY,
            width: contentWidth,
            height: imgHeight,
            srcWidth,
            srcHeight,
            pageHeight,
            safeCutPoints
          });
        } else {
          if (currentY + imgHeight > pageHeight - MARGIN && currentY > MARGIN) {
            pdf.addPage();
            currentY = MARGIN;
          }
          placeImage(pdf, dataUrl, currentY, contentWidth, imgHeight);
          currentY += imgHeight;
        }

        currentY += BLOCK_GAP;
      }

      pdf.save("analytics-report.pdf");
    } catch (err) {
      console.error("Export Error:", err);
    } finally {
      document.body.removeChild(sandbox);
      setLoading(false);
    }
  };

  return { exportPdf, pdfLoading: loading };
}
